import React from 'react';
import { PaymentMethod } from '../../../../../../models/interfaces/PaymentMethod.interface';
import { makeStyles } from '@mui/styles';
import { customTheme } from '../../../../../../styles/theme';
import logger from '../../../../../../commons/logger';
import Paper from '@mui/material/Paper';
import { Button, MenuItem, TextField, Toolbar } from '@mui/material';
import Typography from '@mui/material/Typography';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import { InvoiceDetail } from '../../../../../../models/interfaces/InvoiceDetail.interface';
import { PAYMENT_STATUS } from '../../../../../../models/enums/PaymentStatus.enum';
import adminService from '../../../../../../services/admin.service';

const useStyles = makeStyles({
  paper: {
    marginBottom: '20px',
    padding: '20px',
    justifyContent: 'center',
    display: 'flex',
    alignItems: 'flex-end',
    flexDirection: 'column',
  },
  toolbar: {
    width: '100%',
    padding: 0,
  },
  title: {
    flexGrow: 1,
  },
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: customTheme.spacing(1),
  },
  submit: {
    margin: customTheme.spacing(3, 0, 2),
  },
});

interface UserPaymentFormComponentProps {
  paymentMethods: PaymentMethod[];
  userId: number;
  userEmail: string;
}

export default function UserPaymentFormComponent(props: UserPaymentFormComponentProps) {
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const [userId, setUserId] = React.useState<number>(null);
  const [userEmail, setUserEmail] = React.useState<string>(null);
  const [paymentMethods, setPaymentMethods] = React.useState<PaymentMethod[]>([]);
  const [openInvoicesOfUser, setOpenInvoicesOfUser] = React.useState<InvoiceDetail[]>([]);

  const [selectedInvoiceId, setSelectedInvoiceId] = React.useState<string>(null);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = React.useState<string>(null);

  const _getAllOpenInvoicesOfUser = async (userEmail: string) => {
    const invoicePayments = await adminService.fetchInvoicePaymentsOfUser(userEmail);
    const setOfCompletedInvoiceId = new Set<string>(
      invoicePayments
        ?.filter(p => p.paypalOrderStatus === 'COMPLETED' || p.stripePaymentStatus === PAYMENT_STATUS.SUCCEEDED)
        ?.map(p => p.invoiceId)
    );

    const invoices = await adminService.fetchAllInvoicesOfUser(userEmail);

    const openInvoices = invoices?.filter(
      invoiceDetails =>
        (invoiceDetails.voucherStatus === 'open' || invoiceDetails.voucherStatus === 'overdue') &&
        !setOfCompletedInvoiceId.has(invoiceDetails.id)
    );
    setOpenInvoicesOfUser(openInvoices || []);
  };

  const _initUserInvoices = async (userEmail: string) => {
    setIsSubmitting(true);

    try {
      if (userEmail) {
        await _getAllOpenInvoicesOfUser(userEmail);
      }
    } catch (e) {
      logger.error(e);
    } finally {
      setIsSubmitting(false);
    }
  };

  React.useEffect(() => {
    setUserId(props?.userId);
    setUserEmail(props?.userEmail);
    setPaymentMethods(props?.paymentMethods || []);
    setSelectedPaymentMethodId(props?.paymentMethods?.find(p => p.isDefault)?.id);
  }, [props]);

  React.useEffect(() => {
    _initUserInvoices(userEmail);
  }, [userEmail]);

  const submit = async (event: any) => {
    event.preventDefault();

    setIsSubmitting(true);

    try {
      const response = await adminService.chargeInvoiceOfUserOverStripe({
        userId,
        userEmail,
        invoiceId: selectedInvoiceId,
        paymentMethodId: selectedPaymentMethodId,
      });

      if (response) {
        setSelectedPaymentMethodId(props?.paymentMethods?.find(p => p.isDefault)?.id);
        setSelectedInvoiceId(null);
        await _getAllOpenInvoicesOfUser(userEmail);
      }
    } catch (e) {
      logger.error(e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const classes = useStyles();

  return (
    <Paper className={classes.paper}>
      <Toolbar className={classes.toolbar}>
        <Typography variant="h6" className={classes.title}>
          Charge invoice of customer manually
        </Typography>
      </Toolbar>

      <form className={classes.form} onSubmit={submit}>
        {/*Open invoice*/}
        <TextField
          variant="outlined"
          margin="dense"
          required
          select
          fullWidth
          label="Invoice"
          name="invoice"
          value={selectedInvoiceId || ''}
          onChange={(event: any) => setSelectedInvoiceId(event.target?.value)}
          helperText={<span>Only open or overdue invoices can be selected</span>}
        >
          {openInvoicesOfUser?.map(invoiceDetails => (
            <MenuItem value={invoiceDetails.id} key={invoiceDetails.id}>
              <span>[{invoiceDetails.voucherNumber}]</span> <span>[{invoiceDetails.totalAmount} EUR]</span>{' '}
              <span style={{ color: 'grey' }}>Due date: {invoiceDetails.dueDate}</span>
            </MenuItem>
          ))}
        </TextField>

        {/*Payment method*/}
        <TextField
          variant="outlined"
          margin="dense"
          required
          select
          fullWidth
          label="Payment method"
          name="paymentMethod"
          value={selectedPaymentMethodId || ''}
          onChange={(event: any) => setSelectedPaymentMethodId(event.target?.value)}
          helperText={<span>The default payment method will be selected initially</span>}
        >
          {paymentMethods.map(paymentMethod => (
            <MenuItem value={paymentMethod.id} key={paymentMethod.id}>
              <span>[{paymentMethod.paymentMethodType}]</span>{' '}
              <span>*********{paymentMethod.additionalProviderPaymentMethodData?.last4}</span>{' '}
              {paymentMethod.isDefault ? <span style={{ color: 'grey' }}>(DEFAULT)</span> : <></>}
            </MenuItem>
          ))}
        </TextField>

        {/*Submit button*/}
        {
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            className={classes.submit}
            disabled={isSubmitting || !selectedInvoiceId || !selectedPaymentMethodId}
            startIcon={<AddShoppingCartIcon />}
          >
            Submit
          </Button>
        }
      </form>
    </Paper>
  );
}
